"use client";

import type React from "react";

interface RichTextDisplayProps {
  content?: string | null;
  className?: string;
  fallbackText?: string;
}

export function RichTextDisplay({
  content,
  className = "",
  fallbackText = "No content available",
}: RichTextDisplayProps) {
  // Handle null, undefined, or empty content
  if (!content || content.trim() === "") {
    return <div className={`${className}`}>{fallbackText}</div>;
  }

  // Check if content contains HTML tags
  const isHTML = /<[^>]*>/g.test(content);

  if (isHTML) {
    // Process the HTML content to ensure proper styling for embedded elements
    let processedContent = content;

    // Ensure images have proper styling and error handling
    processedContent = processedContent.replace(
      /<img([^>]*?)>/g,
      (match, attributes) => {
        // Extract src attribute
        const srcMatch = attributes.match(/src=["']([^"']*)["']/);
        const src = srcMatch ? srcMatch[1] : "";

        // Extract alt attribute or use default
        const altMatch = attributes.match(/alt=["']([^"']*)["']/);
        const alt = altMatch ? altMatch[1] : "Course material";

        return `<img src="${src}" alt="${alt}" style="max-width: 100%; height: auto; margin: 16px auto; display: block; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" /><div style="display: none; padding: 20px; background: #f3f4f6; border-radius: 8px; text-align: center; color: #6b7280; margin: 16px auto;">Image could not be loaded</div>`;
      }
    );

    // Ensure YouTube embeds have proper styling
    processedContent = processedContent.replace(
      /<div class="youtube-embed"[^>]*>/g,
      '<div class="youtube-embed" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 16px 0; border: 2px solid #e2e8f0; border-radius: 8px; background: #f8fafc;">'
    );

    // Ensure iframes have proper styling
    processedContent = processedContent.replace(
      /<iframe([^>]*?)>/g,
      '<iframe$1 style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 6px;">'
    );

    return (
      <div
        className={`prose prose-lg max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: processedContent }}
        style={
          {
            "--tw-prose-body": "rgb(55 65 81)",
            "--tw-prose-headings": "rgb(17 24 39)",
          } as React.CSSProperties
        }
      />
    );
  } else {
    // Render plain text with preserved line breaks and spacing
    return (
      <div className={`whitespace-pre-wrap leading-relaxed ${className}`}>
        {content}
      </div>
    );
  }
}
